/**
 * Shared validation utility — pure functions, no React dependencies.
 *
 * Usage:
 *   import { required, maxLength, validateSchema, isValid } from '../../utils/validation';
 *
 *   const errors = validateSchema(values, {
 *     name: [required('Name is required'), maxLength(100)],
 *   });
 *   if (!isValid(errors)) { ... }
 *
 * Rule builders return a validator function: (value, rowContext?) => string | null
 *   null  = passes
 *   string = error message
 *
 * rowContext is an optional reference to the full row object, used by rules that
 * need to inspect sibling fields (e.g. sumGreaterThan).
 */

// ─── Rule Builders ────────────────────────────────────────────────────────────

/**
 * Value must be present (non-null, non-undefined, non-empty string).
 * Numbers including 0 are considered present.
 */
export const required = (msg = 'Required') => (v) => {
  if (v === null || v === undefined) return msg;
  if (typeof v === 'string' && !v.trim()) return msg;
  return null;
};

/**
 * String length (trimmed) must not exceed n characters.
 */
export const maxLength = (n, msg) => (v) => {
  if (v == null || v === '') return null;
  return String(v).trim().length > n ? (msg ?? `Max ${n} characters`) : null;
};

/**
 * Numeric value must be at least n.
 */
export const minValue = (n, msg) => (v) => {
  const num = parseFloat(v);
  if (isNaN(num)) return null; // let required() handle missing values
  return num < n ? (msg ?? `Must be at least ${n}`) : null;
};

/**
 * Numeric value must not exceed n.
 */
export const maxValue = (n, msg) => (v) => {
  const num = parseFloat(v);
  if (isNaN(num)) return null;
  return num > n ? (msg ?? `Must be no more than ${n}`) : null;
};

/**
 * Date string (YYYY-MM-DD) must be present.
 * Alias of required() but with a date-specific default message.
 */
export const dateRequired = (msg = 'Date is required') => required(msg);

/**
 * Validates that endDate is not before startDate.
 * Designed for use on a standalone sentinel field or called directly.
 * Pass both date strings at call time; returns a rule function.
 */
export const dateOrder = (startDate, endDate, msg) => () =>
  startDate && endDate && endDate < startDate
    ? (msg ?? 'End date must be on or after start date')
    : null;

/**
 * Array must contain at least n items.
 */
export const minArrayLength = (n, msg) => (v) =>
  !Array.isArray(v) || v.length < n
    ? (msg ?? `Select at least ${n}`)
    : null;

/**
 * Sum of numeric sibling fields on the row must be greater than min.
 * Requires rowContext to be passed through validateSchema/validateField.
 *
 * @param {string[]} fields  - field names on the row object to sum
 * @param {number}   min     - sum must be strictly greater than this
 */
export const sumGreaterThan = (fields, min, msg) => (_, row) => {
  if (!row) return null;
  const sum = fields.reduce((s, f) => s + (parseFloat(row[f]) || 0), 0);
  return sum <= min ? (msg ?? `Total must be greater than ${min}`) : null;
};

/**
 * Sum of numeric sibling fields on the row must not exceed max.
 * Useful for per-employee or per-day hour cap checks.
 * Requires rowContext to be passed through validateSchema/validateField.
 *
 * @param {string[]} fields  - field names on the row object to sum
 * @param {number}   max     - sum must not exceed this value
 */
export const sumLessThanOrEqual = (fields, max, msg) => (_, row) => {
  if (!row) return null;
  const sum = fields.reduce((s, f) => s + (parseFloat(row[f]) || 0), 0);
  return sum > max ? (msg ?? `Total must not exceed ${max}`) : null;
};

/**
 * Value must match the given regular expression.
 */
export const pattern = (regex, msg = 'Invalid format') => (v) => {
  if (v == null || v === '') return null;
  return regex.test(String(v)) ? null : msg;
};

// ─── Core Runners ─────────────────────────────────────────────────────────────

/**
 * Run a value through an ordered list of rules.
 * Returns the first error message encountered, or null if all pass.
 *
 * @param {*}         value      - the field value to validate
 * @param {Function[]} rules     - array of rule functions
 * @param {object|null} rowContext - optional sibling-field context
 */
export const validateField = (value, rules, rowContext = null) => {
  for (const rule of rules) {
    const err = rule(value, rowContext);
    if (err) return err;
  }
  return null;
};

/**
 * Validate an object against a schema.
 * Returns an errors object containing only the fields that failed.
 * An empty object means all fields passed.
 *
 * @param {object} values     - { fieldName: value, ... }
 * @param {object} schema     - { fieldName: [rule, rule, ...], ... }
 * @param {object} rowContext - passed through to every rule (for sumGreaterThan etc.)
 */
export const validateSchema = (values, schema, rowContext = null) => {
  const errors = {};
  for (const [field, rules] of Object.entries(schema)) {
    const err = validateField(values[field], rules, rowContext);
    if (err) errors[field] = err;
  }
  return errors;
};

/**
 * Returns true when the errors object contains no keys (i.e. all valid).
 */
export const isValid = (errors) => Object.keys(errors).length === 0;
